/**
 * useAccurateTokenCount — Get accurate token count from Gemini countTokens API.
 *
 * Khac voi estimateTokens() (char-based, 70-90% accuracy),
 * hook nay goi Worker API → Gemini countTokens → 99% accuracy.
 *
 * Use case: hien thi so credits chinh xac khi user upload file,
 * truoc khi submit job translate.
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

export interface AccurateTokenCount {
  input_tokens: number;
  estimated_output_tokens: number;
  estimated_total_tokens: number;
  estimated_credits: number;          // Equal to estimated_total_tokens (no safety margin since 2026-04-20)
  output_ratio: number;
  /** @deprecated safety_margin removed in US-04-07 / US-16-03. Kept optional for legacy consumers. */
  safety_margin?: number;
  model: string;
  segments_count: number;
}

interface CountOptions {
  type?: 'translate' | 'qc' | 'audit' | 'semantic-match';
  model?: string;

  // Translate (legacy fields)
  segments?: string[];
  text?: string;
  sourceLang?: string;
  targetLang?: string;

  // QC + Audit
  sourceSegments?: string[];
  aiSegments?: string[];

  // Audit only
  humanSegments?: string[];
  systemInstruction?: string;

  // QC only
  extraRules?: string;

  // Audit billing mode (mirrors backend):
  //   'train_pre_translate_audit' → factor formula
  //   undefined / 'standalone_audit' → real prompt formula
  billing_context?: 'standalone_audit' | 'train_pre_translate_audit';
}

export function useAccurateTokenCount() {
  const { session } = useAuth();
  const [isCounting, setIsCounting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const count = useCallback(async (opts: CountOptions): Promise<AccurateTokenCount | null> => {
    if (!session?.access_token) {
      setError('Not authenticated');
      return null;
    }

    setIsCounting(true);
    setError(null);

    try {
      const res = await fetch(`${WORKERS_URL}/api/count-tokens`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(opts),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        setError((data as any).error || 'Count failed');
        return null;
      }

      return await res.json() as AccurateTokenCount;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setIsCounting(false);
    }
  }, [session?.access_token]);

  const countBundle = useCallback(async (opts: {
    bundle_type: 'translate' | 'train';
    segments: string[];
    humanSegments?: string[];
    sourceLang?: string;
    targetLang?: string;
    // IP protection: KHÔNG truyền model — Worker hardcode server-side
  }): Promise<{
    bundleType: 'translate' | 'train';
    pricingVersion: number | null;
    computedAt: string;
    breakdown: {
      translate: number;
      qc: number | null;
      semanticMatch: number | null;
      audit: number | null;
    };
    factors: { qc: number | null; audit: number | null; semanticMatch: number };
    meta: { translateInputTokens: number; translateBatchCount: number; segmentsCount: number };
  } | null> => {
    if (!session?.access_token) {
      setError('Not authenticated');
      return null;
    }
    setIsCounting(true);
    setError(null);
    try {
      const res = await fetch(`${WORKERS_URL}/api/count-tokens-bundle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(opts),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        setError((data as any).error || 'Bundle count failed');
        return null;
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setIsCounting(false);
    }
  }, [session?.access_token]);

  return { count, countBundle, isCounting, error };
}
